"use client";

import { useReducer, useEffect, useContext, createContext } from "react";
import { v4 as uuid } from "uuid";
import {
  Product,
  Cart,
  CartItem,
  CartItemOption,
  CartRecord,
  CartItemRecord,
  CartItemOptionRecord,
  CartItemChoiceRecord,
  ItemSource,
} from "~/types";
import {
  addCartItemAnalyticsEvent,
  viewItemAnalyticsEvent,
} from "~/utils/analytics";
import useCurrency from "./currency";
import { useGraphql } from "~/api-client";

export function CartProvider(props: {
  locationRef: string;
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] }, (state) => {
    try {
      const prev =
        localStorage && localStorage.getItem(`cart_${props.locationRef}`);

      if (prev) {
        const newState = JSON.parse(prev);
        return newState;
      }
      return state;
    } catch (err) {
      const error = err as unknown as Error;
      if (error.message !== "localStorage is not defined")
        console.error(error.message);
      return state;
    }
  });

  // Used for analytics, non-blocking hooks.
  const currency = useCurrency();
  const { getMenus } = useGraphql();

  useEffect(() => {
    // push what we have to storage after state updates
    const cartString = JSON.stringify(state);

    localStorage.setItem(`cart_${props.locationRef}`, cartString);
  }, [props.locationRef, state]);

  const service: CartService = {
    cart: state,
    addItem: (item) => {
      addCartItemAnalyticsEvent(currency, props.locationRef, [item], getMenus);
      dispatch({ type: "ADD_ITEM", item });
    },
    removeItem: (lineId) => {
      dispatch({ type: "REMOVE_ITEM", lineId });
    },
    updateItem: (lineId, item) => {
      dispatch({ type: "UPDATE_ITEM", lineId, update: item });
    },
    selectCartItem: (cartItem: CartItem) => {
      const inFlightItem = cartItem;
      dispatch({ type: "SELECT_ITEM", inFlightItem });
      return inFlightItem.lineItemId;
    },
    // Product
    selectProduct: (product: Product, source?: ItemSource) => {
      const inFlightItem = createCartItem(product);
      viewItemAnalyticsEvent(
        currency,
        props.locationRef,
        [inFlightItem],
        getMenus,
      );
      dispatch({
        type: "SELECT_ITEM",
        inFlightItem,
        source,
      });

      return inFlightItem.lineItemId;
    },

    updateInFlightCartItem: (cartItem) => {
      dispatch({ type: "UPDATE_INFLIGHT_CART_ITEM", cartItem });
    },
    removeInFlightCartItem: () => {
      dispatch({ type: "REMOVE_INFLIGHT_CART_ITEM" });
    },
    emptyCart: () => {
      localStorage.clear();
      dispatch({ type: "EMPTY" });
    },
    setCart: (cart: Cart, locationRef?: string) => {
      addCartItemAnalyticsEvent(
        currency,
        props.locationRef,
        cart.items,
        getMenus,
      );
      if (!locationRef || locationRef === props.locationRef)
        dispatch({ type: "SET", cart });
      else {
        setLocalCart(cart, locationRef);
      }
    },
  };

  return <cartCtx.Provider value={service}>{props.children}</cartCtx.Provider>;
}

export function useCart(): CartService {
  return useContext(cartCtx);
}

export function createCartItem(
  product: Product,
  initialQuantity = 1,
): CartItem {
  const item: CartItem = {
    lineItemId: uuid(),
    productId: product.id,
    quantity: initialQuantity,
    options: [],
  };

  if (product.options) {
    item.options = product.options.map((o) => {
      const option: CartItemOption = {
        optionId: o.id,
        choices: [],
      };

      option.choices = o.choices
        .filter(
          (choice) =>
            (choice.defaultQuantity ?? 0) > 0 && choice.product.available,
        )
        .map((choice) => {
          return createCartItem(choice.product, choice.defaultQuantity);
        });

      return option;
    });
  }

  return item;
}

interface CartService {
  cart: Cart;
  addItem: (item: CartItem) => void;
  removeItem: (lineId: string) => void;
  updateItem: (lineId: string, item: CartItem) => void;
  selectCartItem: (cartItem: CartItem) => string;
  selectProduct: (product: Product, source?: ItemSource) => string;
  updateInFlightCartItem: (cartItem: CartItem) => void;
  removeInFlightCartItem: () => void;
  emptyCart: () => void;
  setCart: (cart: Cart, locationRef?: string) => void;
}

const noopCartService: CartService = {
  cart: { items: [], inFlightItem: undefined },
  addItem: () => {
    throw new Error("Cart context not setup");
  },
  removeItem: () => {
    throw new Error("Cart context not setup");
  },
  updateItem: () => {
    throw new Error("Cart context not setup");
  },
  selectCartItem: () => {
    throw new Error("Cart context not setup");
  },
  selectProduct: () => {
    throw new Error("Cart context not setup");
  },
  updateInFlightCartItem: () => {
    throw new Error("Cart context not setup");
  },
  removeInFlightCartItem: () => {
    throw new Error("Cart context not setup");
  },
  emptyCart: () => {
    throw new Error("Cart context not setup");
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCart: (_cart: Cart, _locationRef?: string) => {
    throw new Error("Cart context not setup");
  },
};

const cartCtx = createContext<CartService>(noopCartService);

type Action =
  | { type: "SET"; cart: Cart }
  | { type: "ADD_ITEM"; item: CartItem }
  | { type: "REMOVE_ITEM"; lineId: string }
  | { type: "UPDATE_ITEM"; lineId: string; update: CartItem }
  | { type: "SELECT_ITEM"; inFlightItem: CartItem; source?: ItemSource }
  | { type: "UPDATE_INFLIGHT_CART_ITEM"; cartItem: CartItem }
  | { type: "REMOVE_INFLIGHT_CART_ITEM" }
  | { type: "EMPTY" };

function updateInFlight(inFlightSegment: CartItem, update: CartItem): CartItem {
  if (inFlightSegment.lineItemId === update.lineItemId) return update;

  // TODO: could use known path to make more efficient
  return {
    ...inFlightSegment,
    options: inFlightSegment.options.map((o) => {
      return {
        ...o,
        choices: o.choices.map((c) => updateInFlight(c, update)),
      };
    }),
  };
}

function cartReducer(state: Cart, action: Action): Cart {
  switch (action.type) {
    case "SET":
      return {
        ...action.cart,
      };
    case "UPDATE_INFLIGHT_CART_ITEM":
      return {
        ...state,
        inFlightItem:
          state.inFlightItem &&
          updateInFlight(state.inFlightItem, action.cartItem),
      };
    case "REMOVE_INFLIGHT_CART_ITEM":
      return {
        items: state.items,
      };
    case "ADD_ITEM":
      return {
        ...state,
        items: [...state.items, action.item],
        inFlightItem: undefined,
      };
    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((it) => it.lineItemId !== action.lineId),
      };
    case "UPDATE_ITEM":
      return {
        ...state,
        items: state.items.map((it) =>
          it.lineItemId === action.lineId ? action.update : it,
        ),
      };
    case "SELECT_ITEM":
      return {
        ...state,
        inFlightItem: { ...action.inFlightItem, source: action.source },
      };
    case "EMPTY":
      return {
        items: [],
      };
  }
}

export function mapCartRecordToCart(cart: CartRecord): Cart {
  return {
    items: cart.items.map((item) => mapCartItemRecordToCartItem(item)),
    // TODO: Cart doesn't have notes?
  };
}

function mapCartItemRecordToCartItem(item: CartItemRecord): CartItem {
  {
    const cartItem: CartItem = {
      lineItemId: uuid(),
      productId: item.id,
      quantity: item.quantity,
      note: item.note || undefined,
      options: item.options
        ? item.options.map((o) => mapCartItemOptionRecordToCartItemOption(o))
        : [],
    };

    return cartItem;
  }
}

function mapCartItemOptionRecordToCartItemOption(
  optionRecord: CartItemOptionRecord,
): CartItemOption {
  const option: CartItemOption = {
    optionId: optionRecord.id,
    choices: optionRecord.choices.map((c) =>
      mapCartItemChoiceRecordToCartItemChoice(c),
    ),
  };
  return option;
}

function mapCartItemChoiceRecordToCartItemChoice(
  choice: CartItemChoiceRecord,
): CartItem {
  const cartItem: CartItem = {
    lineItemId: uuid(),
    productId: choice.id,
    quantity: choice.quantity,
    options: choice.options
      ? choice.options.map((o) => mapCartItemOptionRecordToCartItemOption(o))
      : [],
    note: undefined,
  };
  return cartItem;
}

// locationRef is expected to be provided, but setLocalCart was made to have its parameter definition match setCart
// because they are alternatively provided to the onSetCart prop in ActivitySection
export function setLocalCart(cart: Cart, locationRef?: string): void {
  const cartString = JSON.stringify(cart);
  localStorage.setItem(`cart_${locationRef}`, cartString);
}
